import type { DiagramNode } from '../../core/types';
import { createEmptyDocument } from '../../core/defaults';
import { validateDocument } from '../../core/validation';

export interface CustomSymbolPreset {
  id: string;
  name: string;
  createdAt: string;
  node: DiagramNode;
}

const KEY = 'owdex-draw:custom-symbols:v1';
const LIMIT = 80;

export function loadCustomSymbols(): CustomSymbolPreset[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) || '[]') as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((item) => {
      if (!item || typeof item !== 'object') return [];
      const row = item as Partial<CustomSymbolPreset>;
      if (typeof row.id !== 'string' || typeof row.name !== 'string' || typeof row.createdAt !== 'string' || !row.node || typeof row.node !== 'object') return [];
      return [row as CustomSymbolPreset];
    }).slice(0, LIMIT);
  } catch { return []; }
}

function persist(items: CustomSymbolPreset[]) {
  try { localStorage.setItem(KEY, JSON.stringify(items.slice(0, LIMIT))); } catch { /* biblioteca pessoal é best-effort */ }
}

export function saveCustomSymbol(name: string, node: DiagramNode): CustomSymbolPreset[] {
  const preset: CustomSymbolPreset = {
    id: crypto.randomUUID(), name: name.trim().slice(0, 80) || node.text.trim().slice(0, 80) || 'Símbolo personalizado', createdAt: new Date().toISOString(),
    node: { ...structuredClone(node), id: crypto.randomUUID(), x: 0, y: 0, locked: false, hidden: false, groupId: null },
  };
  const next = [preset, ...loadCustomSymbols()]; persist(next); return next;
}

export function deleteCustomSymbol(id: string): CustomSymbolPreset[] {
  const next = loadCustomSymbols().filter((item) => item.id !== id); persist(next); return next;
}

export function exportCustomSymbolsJson(): string {
  return JSON.stringify({ format: 'owdex-draw-custom-library', version: 1, symbols: loadCustomSymbols() }, null, 2);
}

export function importCustomSymbolsJson(text: string): CustomSymbolPreset[] {
  const parsed = JSON.parse(text) as unknown;
  if (!parsed || typeof parsed !== 'object') throw new Error('Biblioteca inválida.');
  const root = parsed as Record<string, unknown>;
  if (root.format !== 'owdex-draw-custom-library' || root.version !== 1 || !Array.isArray(root.symbols)) throw new Error('Formato de biblioteca não reconhecido.');
  const symbols = root.symbols.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const row = item as Partial<CustomSymbolPreset>;
    if (typeof row.id !== 'string' || typeof row.name !== 'string' || typeof row.createdAt !== 'string' || !row.node || typeof row.node !== 'object') return [];
    const node = row.node as DiagramNode;
    if (typeof node.id !== 'string' || typeof node.type !== 'string' || typeof node.text !== 'string' || !node.style || typeof node.style !== 'object') return [];
    const candidate = createEmptyDocument(); candidate.pages[0].nodes = [structuredClone(node)];
    if (!validateDocument(candidate).valid) return [];
    return [{ id: crypto.randomUUID(), name: row.name.slice(0, 80), createdAt: new Date().toISOString(), node: { ...structuredClone(node), id: crypto.randomUUID(), x: 0, y: 0, locked: false, hidden: false, groupId: null } } satisfies CustomSymbolPreset];
  }).slice(0, LIMIT);
  if (symbols.length === 0 && root.symbols.length > 0) throw new Error('Nenhum símbolo válido foi encontrado no arquivo.');
  persist(symbols); return symbols;
}
